import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  EMPTY,
  interval,
  Observable,
  Subject,
  Subscription,
} from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  AddReactionRequest,
  Channel,
  ChannelDetail,
  ChannelType,
  CreateChannelRequest,
  EntityResolveResult,
  Message,
  PaginatedChannels,
  PaginatedMessages,
  QueuedOutboundMessage,
  SendMessageRequest,
  WsEvent,
  WsSendMessagePayload,
} from '../models/chat.models';

export type FooterChannelsSubTab = 'group' | 'entity_linked';

export interface OpenFooterChatRequest {
  channelId: number | null;
  channelsSubTab?: FooterChannelsSubTab;
}

@Injectable({ providedIn: 'root' })
export class ChatService implements OnDestroy {
  /**
   * Same API host as the monolith. Traefik PathPrefix(`/api/chat`) routes
   * chat REST/WS to the chat microservice; other paths stay on the monolith.
   */
  private readonly chatBaseUrl = environment.chatApiUrl;
  /** Monolith — entity registry, storage, auth/users. */
  private readonly baseUrl = environment.fastApiUrl;
  private readonly searchUsersUrl = `${environment.adminServiceBasePath}`;

  // WebSocket state
  private ws: WebSocket | null = null;
  private wsConnected = false;
  private wsAuthenticated = false;
  private pendingSubscriptions: Set<number> = new Set();
  private reconnectTimer?: ReturnType<typeof setTimeout>;
  private pingSubscription?: Subscription;

  private lastPongAt = 0;

  private pongTimeoutTimer?: ReturnType<typeof setTimeout>;

  private readonly PING_INTERVAL = 30_000;

  private readonly PONG_TIMEOUT = 10_000;

  private isOnline = navigator.onLine;

  private manualDisconnect = false;

  private reconnectAttempts = 0;

  private hadUnexpectedDisconnect = false;

  private static readonly OUTBOUND_QUEUE_KEY = 'chat_outbound_queue';

  readonly connectionStatus$ = new BehaviorSubject<
    'online' | 'offline' | 'connecting' | 'connected' | 'reconnecting'
  >(navigator.onLine ? 'online' : 'offline');

  readonly reconnected$ = new Subject<void>();

  readonly wsEvents$ = new Subject<WsEvent>();

  /**
   * Map of channel_id → unread count.
   */
  readonly unreadCounts$ =
    new BehaviorSubject<Map<number, number>>(new Map());

  /**
   * Total unread — convenience observable for the badge.
   */
  readonly totalUnread$ =
    new BehaviorSubject<number>(0);

  /**
   * Signal to open the chat modal, optionally pre-selecting a channel.
   */
  readonly openChatModal$ =
    new Subject<number | null>();

  /**
   * Channel currently visible in the chat window.
   */
  private activeChannelId: number | null = null;
  private activeChannelVisible = false;

  /**
   * channel_id → channel_type — needed to split footer badge totals
   * even when the channel-list panel is closed.
   */
  private channelTypeById = new Map<number, ChannelType>();

  /** Ensures we only bootstrap unread subscriptions once per session. */
  private unreadTrackingBootstrapped = false;

  readonly directUnread$ =
    new BehaviorSubject<number>(0);

  readonly channelUnread$ =
    new BehaviorSubject<number>(0);

  private openFooterChatSubject =
    new Subject<OpenFooterChatRequest>();

  openFooterChat$ =
    this.openFooterChatSubject.asObservable();

  readonly inAppNotification$ =
    new Subject<{ message: Message; channelId: number }>();

  private channelActivitySubject =
    new Subject<{
      channelId: number;
      lastMessage: string;
      lastMessageAt: string;
    }>();

  channelActivity$ =
    this.channelActivitySubject.asObservable();

  // ───────────────────────────────────────────────────────────────────────────
  // Authentication / outbound queue
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Frames waiting until authentication succeeds.
   *
   * Keep this separate from pendingMessages.
   */
  private pendingOutbound: object[] = [];

  private authWaiters: Array<(ok: boolean) => void> = [];

  private authFallbackTimer?: ReturnType<typeof setTimeout>;

  /**
   * Messages that could not be delivered / acknowledged yet.
   *
   * Kept until message_ack / new_message with matching client_id.
   * Persisted to localStorage so they survive page refresh.
   */
  private pendingMessages: QueuedOutboundMessage[] = [];

  // ───────────────────────────────────────────────────────────────────────────
  // Constructor
  // ───────────────────────────────────────────────────────────────────────────

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    window.addEventListener(
      'online',
      this.handleNetworkOnline
    );

    window.addEventListener(
      'offline',
      this.handleNetworkOffline
    );

    this.isOnline = navigator.onLine;

    this.connectionStatus$.next(
      this.isOnline ? 'online' : 'offline'
    );

    this.loadOutboundQueue();

    if (typeof window !== 'undefined' && 'Notification' in window) {
      const askPermissionOnFirstGesture = () => {
        if (Notification.permission === 'default') {
          this.requestNotificationPermission();
        }
        window.removeEventListener('click', askPermissionOnFirstGesture);
        window.removeEventListener('keydown', askPermissionOnFirstGesture);
      };
      window.addEventListener('click', askPermissionOnFirstGesture, { once: true });
      window.addEventListener('keydown', askPermissionOnFirstGesture, { once: true });
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Auth helpers
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * True when optplanner_user has both token and companyId.
   */
  hasAuthContext(): boolean {
    return this.getAuthContext() !== null;
  }

  getAuthContext(): {
    token: string;
    companyId: string;
  } | null {
    try {
      const raw =
        localStorage.getItem('optplanner_user');

      if (!raw) {
        return null;
      }

      const data = JSON.parse(raw);

      const token = data.token || '';

      const companyId =
        data.companyId ||
        data.company_id ||
        '';

      if (!token || !companyId) {
        return null;
      }

      return {
        token,
        companyId,
      };
    } catch {
      return null;
    }
  }

  private getHeaders(): HttpHeaders | null {
    const userData = this.getAuthContext();

    if (!userData) {
      return null;
    }

    return new HttpHeaders({
      'Content-Type': 'application/json',
      'x-company-id': userData.companyId,
    });
  }

  private handleNetworkOffline = (): void => {
    console.log('[Chat WS] Network disconnected');

    this.isOnline = false;

    this.connectionStatus$.next('offline');

    /**
     * There is no reason to keep sending heartbeat messages when
     * the browser itself reports that the network is unavailable.
     */
    this.stopPing();

    clearTimeout(this.pongTimeoutTimer);
    this.pongTimeoutTimer = undefined;

    /**
     * Close the socket so we recover faster when network returns.
     * Detach first so onclose does not race with a second reconnect path.
     */
    const socket = this.ws;
    if (socket) {
      this.hadUnexpectedDisconnect = true;
      this.ws = null;
      this.wsConnected = false;
      this.wsAuthenticated = false;
      try {
        socket.close(1000, 'Browser offline');
      } catch {
      }
    }
  };

  private handleNetworkOnline = (): void => {
    console.log('[Chat WS] Network restored');

    this.isOnline = true;

    if (this.manualDisconnect) {
      return;
    }

    this.connectionStatus$.next('reconnecting');

    /**
     * If there is a pending reconnect timer, cancel it because we're going
     * to attempt the connection immediately.
     */
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;

    if (
      !this.ws ||
      this.ws.readyState === WebSocket.CLOSED ||
      this.ws.readyState === WebSocket.CLOSING
    ) {
      this.connectWebSocket();
    }
  };

  // ───────────────────────────────────────────────────────────────────────────
  // WebSocket connection
  // ───────────────────────────────────────────────────────────────────────────

  connectWebSocket(): void {
    /**
     * An explicit call to connect starts a new WebSocket lifecycle.
     */
    this.manualDisconnect = false;

    /**
     * navigator.onLine is used as the first-level network check.
     *
     * This does NOT mean that the internet/backend is reachable.
     * Ping/pong handles that.
     */
    if (!this.isOnline || !navigator.onLine) {
      this.isOnline = false;

      this.connectionStatus$.next('offline');

      return;
    }

    /**
     * Don't create duplicate WebSockets.
     */
    if (
      this.ws &&
      (
        this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING
      )
    ) {
      return;
    }

    const userData = this.getAuthContext();

    if (!userData) {
      return;
    }

    this.isOnline = true;

    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;

    clearTimeout(this.authFallbackTimer);

    clearTimeout(this.pongTimeoutTimer);
    this.pongTimeoutTimer = undefined;

    this.wsConnected = false;
    this.wsAuthenticated = false;

    this.connectionStatus$.next(
      this.reconnectAttempts > 0
        ? 'reconnecting'
        : 'connecting'
    );

    const wsBase =
      this.baseUrl
        .replace(/^https:\/\//, 'wss://')
        .replace(/^http:\/\//, 'ws://');

    const url = `${wsBase}/api/chat/ws`;

    console.log('[Chat WS] Creating WebSocket connection');

    const socket = new WebSocket(url);

    this.ws = socket;

    // ────────────────────────────────────────────────────────────────────────
    // WebSocket OPEN
    // ────────────────────────────────────────────────────────────────────────

    socket.onopen = () => {
      if (this.ws !== socket) {
        return;
      }

      console.log('[Chat WS] WebSocket connected');

      this.connectionStatus$.next('connecting');

      /**
       * Authenticate through the WebSocket.
       */
      this._sendWs({
        type: 'auth',
        token: userData.token,
        company_id: userData.companyId,
      });

      /**
       * Fallback for existing backend behavior.
       *
       * If your backend ALWAYS returns auth_ok, you can eventually remove
       * this fallback.
       */
      clearTimeout(this.authFallbackTimer);

      this.authFallbackTimer =
        setTimeout(() => {
          if (
            this.ws === socket &&
            socket.readyState === WebSocket.OPEN &&
            !this.wsConnected
          ) {
            console.warn(
              '[Chat WS] No auth_ok from server; proceeding because socket is still open.'
            );

            this.markAuthenticated();
          }
        }, 1500);
    };

    // ────────────────────────────────────────────────────────────────────────
    // WebSocket MESSAGE
    // ────────────────────────────────────────────────────────────────────────

    socket.onmessage = (event) => {
      if (this.ws !== socket) {
        return;
      }

      try {
        const data =
          JSON.parse(event.data) as WsEvent & {
            type: string;
            message?: string;
          };

        // ────────────────────────────────────────────────────────────────────
        // HEARTBEAT RESPONSE
        // ────────────────────────────────────────────────────────────────────

        if (data.type === 'pong') {
          this.lastPongAt = Date.now();

          clearTimeout(this.pongTimeoutTimer);
          this.pongTimeoutTimer = undefined;

          console.log('[Chat WS] Pong received');

          return;
        }

        // ────────────────────────────────────────────────────────────────────
        // AUTH SUCCESS
        // ────────────────────────────────────────────────────────────────────

        if (this.isAuthSuccess(data.type)) {
          clearTimeout(this.authFallbackTimer);

          this.markAuthenticated();

          return;
        }

        // ────────────────────────────────────────────────────────────────────
        // AUTH FAILURE
        // ────────────────────────────────────────────────────────────────────

        if (this.isAuthFailure(data.type)) {
          clearTimeout(this.authFallbackTimer);

          console.error(
            '[Chat WS] Auth failed:',
            data
          );

          this.rejectAuthWaiters();

          this.wsConnected = false;
          this.wsAuthenticated = false;

          this.wsEvents$.next(
            data as WsEvent
          );

          return;
        }

        // ────────────────────────────────────────────────────────────────────
        // OTHER SERVER EVENTS
        // ────────────────────────────────────────────────────────────────────

        /**
         * Any other inbound frame after auth implies the connection is usable.
         */
        if (!this.wsConnected) {
          clearTimeout(this.authFallbackTimer);

          this.markAuthenticated();
        }

        if (data.type === 'message_ack' && (data as any).client_id) {
          this.removeFromPendingQueue((data as any).client_id);
        }

        if (data.type === 'new_message') {
          const msg = (data as any).message;
          if (msg?.client_id) {
            this.removeFromPendingQueue(msg.client_id);
          }
        }


        if (data.type === 'new_message') {
          const currentUserId =
            this.getCurrentUserId();

          const isOwnMessage =
            (data as any).message?.sender_id ===
            currentUserId;

          const channelId = (data as any)
            .channel_id as number;

          const isActiveChannel =
            channelId === this.activeChannelId;

          const isVisibleChannel =
            isActiveChannel && this.activeChannelVisible;

          const isDocumentHidden =
            typeof document !== 'undefined' &&
            (document.hidden || (typeof document.hasFocus === 'function' && !document.hasFocus()));

          if (!isOwnMessage) {
            if (isDocumentHidden) {
              // User is away from the screen (tab in background or window unfocused):
              // Trigger browser push notification only, do not show in-app toaster card
              this.incrementUnread(channelId);
              this.playNotificationSound();
              this.sendDesktopNotification((data as any).message, channelId);
            } else {
              // User is in the current tab:
              if (!isVisibleChannel) {
                // Trigger in-app toaster card only when channel is not active/visible, do not send browser push notification
                this.incrementUnread(channelId);
                this.playNotificationSound();
                this.inAppNotification$.next({
                  message: (data as any).message,
                  channelId
                });
              } else {
                this.clearUnread(channelId);
              }
            }
          }
        }

        if (data.type === 'mark_read_ack') {
          this.clearUnread(
            (data as any).channel_id
          );
        }

        if (
          data.type === 'channel_added' ||
          data.type === 'member_added'
        ) {
          const channelId = (data as any)
            .channel_id as number;
          const channelType = (data as any)
            .channel_type as ChannelType | undefined;

          if (channelType) {
            this.channelTypeById.set(
              channelId,
              channelType
            );
          }

          this.subscribeToChannel(channelId);
        }

        this.wsEvents$.next(
          data as WsEvent
        );
      } catch {
        // Ignore malformed WebSocket messages.
      }
    };

    // ────────────────────────────────────────────────────────────────────────
    // WebSocket ERROR
    // ────────────────────────────────────────────────────────────────────────

    socket.onerror = (error) => {
      console.warn(
        '[Chat WS] WebSocket error',
        error
      );

      /**
       * Usually onclose follows onerror.
       *
       * We let onclose handle the actual reconnect lifecycle.
       */
    };

    // ────────────────────────────────────────────────────────────────────────
    // WebSocket CLOSE
    // ────────────────────────────────────────────────────────────────────────

    socket.onclose = () => {
      if (this.ws !== socket) {
        return;
      }

      console.log('[Chat WS] WebSocket closed');

      clearTimeout(this.authFallbackTimer);

      clearTimeout(this.pongTimeoutTimer);
      this.pongTimeoutTimer = undefined;

      this.wsConnected = false;
      this.wsAuthenticated = false;

      this.ws = null;

      this.stopPing();

      this.rejectAuthWaiters();

      /**
       * Do not reconnect after intentional logout/manual disconnect.
       */
      if (this.manualDisconnect) {
        return;
      }

      this.hadUnexpectedDisconnect = true;

      /**
       * If browser says network is unavailable, don't start reconnect
       * timers. Wait for the browser's online event.
       */
      if (!navigator.onLine) {
        this.isOnline = false;

        this.connectionStatus$.next('offline');

        console.log(
          '[Chat WS] Network is offline; waiting for network restoration.'
        );

        return;
      }

      /**
       * Browser says network is available.
       *
       * The WebSocket itself is unavailable, so start reconnecting.
       */
      if (this.getAuthContext()) {
        this.scheduleReconnect();
      }
    };
  }

  // ───────────────────────────────────────────────────────────────────────────
  // WebSocket authentication
  // ───────────────────────────────────────────────────────────────────────────

  private isAuthSuccess(type: string): boolean {
    return (
      type === 'auth_ok' ||
      type === 'authenticated' ||
      type === 'auth_success'
    );
  }

  private isAuthFailure(type: string): boolean {
    return (
      type === 'auth_error' ||
      type === 'auth_failed' ||
      type === 'unauthorized'
    );
  }

  private markAuthenticated(): void {
    /**
     * Prevent duplicate calls from:
     *
     * auth_ok
     * and
     * another server event
     */
    if (this.wsConnected && this.wsAuthenticated) {
      return;
    }

    this.wsAuthenticated = true;
    this.wsConnected = true;

    /**
     * Successful authentication means reconnect lifecycle succeeded.
     */
    this.reconnectAttempts = 0;

    this.connectionStatus$.next('connected');

    console.log(
      '[Chat WS] WebSocket authenticated and ready'
    );

    this.requestNotificationPermission();

    // ────────────────────────────────────────────────────────────────────────
    // RESUBSCRIBE CHANNELS
    // ────────────────────────────────────────────────────────────────────────

    this.pendingSubscriptions.forEach(
      (channelId) => {
        this._sendWs({
          type: 'subscribe',
          channel_id: channelId,
        });
      }
    );

    // ────────────────────────────────────────────────────────────────────────
    // FLUSH AUTH-WAITING FRAMES
    // ────────────────────────────────────────────────────────────────────────

    const queued =
      this.pendingOutbound.splice(0);

    queued.forEach((payload) => {
      this._sendWs(payload);
    });

    // ────────────────────────────────────────────────────────────────────────
    // FLUSH PENDING MESSAGES
    // ────────────────────────────────────────────────────────────────────────

    this.flushPendingMessages();

    // ────────────────────────────────────────────────────────────────────────
    // START HEARTBEAT
    // ────────────────────────────────────────────────────────────────────────

    this.startPing();

    // ────────────────────────────────────────────────────────────────────────
    // GAP SYNC SIGNAL
    // ────────────────────────────────────────────────────────────────────────

    if (this.hadUnexpectedDisconnect) {
      this.hadUnexpectedDisconnect = false;
      this.reconnected$.next();
    }

    // ────────────────────────────────────────────────────────────────────────
    // RESOLVE AUTH WAITERS
    // ────────────────────────────────────────────────────────────────────────

    const waiters =
      this.authWaiters.splice(0);

    waiters.forEach(
      (resolve) => resolve(true)
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Reconnect
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Schedule a reconnect with exponential backoff.
   */
  private scheduleReconnect(): void {
    if (
      this.manualDisconnect ||
      !navigator.onLine
    ) {
      return;
    }

    /**
     * Avoid duplicate reconnect timers.
     */
    if (this.reconnectTimer) {
      return;
    }

    const delay = Math.min(
      1000 *
        Math.pow(
          2,
          this.reconnectAttempts
        ),
      30_000
    );

    console.log(
      `[Chat WS] Reconnecting in ${delay}ms`
    );

    this.connectionStatus$.next(
      'reconnecting'
    );

    this.reconnectTimer =
      setTimeout(() => {
        this.reconnectTimer = undefined;

        this.reconnectAttempts++;

        this.connectWebSocket();
      }, delay);
  }

  /**
   * Called when an established WebSocket is technically OPEN but
   * the backend has stopped responding.
   *
   * This is the important part for:
   *
   * WiFi connected
   * +
   * Internet unavailable
   */
  private handleUnresponsiveConnection(): void {
    if (this.manualDisconnect) {
      return;
    }

    console.warn(
      '[Chat WS] WebSocket is unresponsive'
    );

    this.wsConnected = false;
    this.wsAuthenticated = false;

    this.connectionStatus$.next(
      'reconnecting'
    );

    const socket = this.ws;

    /**
     * Detach the socket first so its onclose handler doesn't try to
     * start another reconnect flow.
     */
    this.ws = null;

    this.hadUnexpectedDisconnect = true;

    this.stopPing();

    clearTimeout(this.pongTimeoutTimer);
    this.pongTimeoutTimer = undefined;

    if (socket) {
      try {
        socket.close(
          1000,
          'WebSocket heartbeat timeout'
        );
      } catch {
        // Ignore close errors.
      }
    }

    /**
     * navigator.onLine may still be TRUE here.
     *
     * That is expected.
     *
     * The heartbeat already proved that the backend is not reachable.
     */
    if (
      navigator.onLine &&
      this.getAuthContext()
    ) {
      this.scheduleReconnect();
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Auth wait
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Resolves true when the socket is authenticated,
   * or false on timeout / close.
   */
  private waitUntilAuthenticated(
    timeoutMs = 8000
  ): Promise<boolean> {
    if (this.isWebSocketConnected()) {
      return Promise.resolve(true);
    }

    this.connectWebSocket();

    return new Promise((resolve) => {
      let resolved = false;

      const finish = (result: boolean): void => {
        if (resolved) {
          return;
        }

        resolved = true;

        clearTimeout(timer);

        const idx =
          this.authWaiters.indexOf(
            onReady
          );

        if (idx >= 0) {
          this.authWaiters.splice(
            idx,
            1
          );
        }

        resolve(result);
      };

      const timer =
        setTimeout(() => {
          finish(
            this.isWebSocketConnected()
          );
        }, timeoutMs);

      const onReady = (
        ok: boolean
      ) => {
        finish(ok);
      };

      this.authWaiters.push(onReady);

      /**
       * Connection may have become ready between connectWebSocket()
       * and registering the waiter.
       */
      if (this.isWebSocketConnected()) {
        finish(true);
      }
    });
  }

  private rejectAuthWaiters(): void {
    const waiters =
      this.authWaiters.splice(0);

    waiters.forEach(
      (resolve) => resolve(false)
    );

    /**
     * IMPORTANT:
     *
     * Do NOT clear pendingOutbound here.
     *
     * An unexpected disconnect should not automatically throw away
     * frames waiting for the connection.
     *
     * pendingOutbound is cleared during intentional logout.
     */
  }

  // ───────────────────────────────────────────────────────────────────────────
  // WebSocket subscriptions
  // ───────────────────────────────────────────────────────────────────────────

  subscribeToChannel(
    channelId: number
  ): void {
    /**
     * Always remember the subscription.
     *
     * This allows us to restore it after reconnect.
     */
    this.pendingSubscriptions.add(
      channelId
    );

    if (this.wsConnected) {
      this._sendWs({
        type: 'subscribe',
        channel_id: channelId,
      });
    }
  }

  unsubscribeFromChannel(
    channelId: number
  ): void {
    this.pendingSubscriptions.delete(
      channelId
    );

    if (this.wsConnected) {
      this._sendWs({
        type: 'unsubscribe',
        channel_id: channelId,
      });
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // WebSocket disconnect
  // ───────────────────────────────────────────────────────────────────────────

  disconnectWebSocket(): void {
    /**
     * This is an intentional disconnect.
     *
     * Therefore automatic reconnect must NOT happen.
     */
    this.manualDisconnect = true;

    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;

    clearTimeout(this.authFallbackTimer);

    clearTimeout(this.pongTimeoutTimer);
    this.pongTimeoutTimer = undefined;

    this.stopPing();

    this.rejectAuthWaiters();

    /**
     * Clear everything because this is an intentional logout/manual
     * disconnect.
     */
    this.pendingSubscriptions.clear();
    this.pendingOutbound = [];
    this.pendingMessages = [];
    this.persistOutboundQueue();
    this.hadUnexpectedDisconnect = false;

    this.activeChannelId = null;

    this.unreadCounts$.next(
      new Map()
    );

    this.channelTypeById.clear();
    this.unreadTrackingBootstrapped = false;

    this.directUnread$.next(0);
    this.channelUnread$.next(0);
    this.totalUnread$.next(0);

    const socket = this.ws;

    this.ws = null;

    this.wsConnected = false;
    this.wsAuthenticated = false;

    this.connectionStatus$.next(
      'offline'
    );

    socket?.close(
      1000,
      'User logout'
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  // WebSocket send
  // ───────────────────────────────────────────────────────────────────────────

  private _sendWs(payload: object): void {
    if (
      this.ws &&
      this.ws.readyState === WebSocket.OPEN
    ) {
      this.ws.send(
        JSON.stringify(payload)
      );
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Current user
  // ───────────────────────────────────────────────────────────────────────────

  private getCurrentUserId(): number {
    try {
      const data =
        JSON.parse(
          localStorage.getItem(
            'optplanner_user'
          ) || '{}'
        );

      return data.id || 0;
    } catch {
      return 0;
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Notification sound
  // ───────────────────────────────────────────────────────────────────────────

  private playNotificationSound(): void {
    try {
      const AudioCtx =
        window.AudioContext ||
        (window as any).webkitAudioContext;

      if (!AudioCtx) {
        return;
      }

      const ctx = new AudioCtx();

      const now =
        ctx.currentTime;

      const osc =
        ctx.createOscillator();

      const gain =
        ctx.createGain();

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.type = 'sine';

      osc.frequency.setValueAtTime(
        1047,
        now
      );

      osc.frequency.setValueAtTime(
        1319,
        now + 0.12
      );

      gain.gain.setValueAtTime(
        0,
        now
      );

      gain.gain.linearRampToValueAtTime(
        0.25,
        now + 0.02
      );

      gain.gain.setValueAtTime(
        0.25,
        now + 0.12
      );

      gain.gain.linearRampToValueAtTime(
        0.25,
        now + 0.14
      );

      gain.gain.exponentialRampToValueAtTime(
        0.001,
        now + 0.55
      );

      osc.start(now);
      osc.stop(now + 0.55);

      osc.onended = () => {
        ctx.close();
      };
    } catch {
      // AudioContext unavailable or blocked.
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Desktop Push Notifications
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Request browser push notification permission if available and pending ('default').
   */
  requestNotificationPermission(): Promise<NotificationPermission> {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      console.warn('[Chat WS] Notification API not supported by browser.');
      return Promise.resolve('denied' as NotificationPermission);
    }

    console.log('[Chat WS] Checking Notification permission status:', Notification.permission);
    if (Notification.permission === 'default') {
      return Notification.requestPermission()
        .then((perm) => {
          console.log('[Chat WS] Notification permission requested. Result:', perm);
          return perm;
        })
        .catch((err) => {
          console.warn('[Chat WS] Request notification permission error:', err);
          return 'denied' as NotificationPermission;
        });
    }
    return Promise.resolve(Notification.permission);
  }

  /**
   * Send desktop push notification when a new message arrives.
   */
  sendDesktopNotification(message: any, channelId: number): void {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      console.warn('[Chat WS] Push notification skipped: Notification API not supported');
      return;
    }

    console.log('[Chat WS] Preparing desktop push notification for message. Permission status:', Notification.permission);

    if (Notification.permission !== 'granted') {
      console.warn(
        '[Chat WS] Desktop push notification blocked because permission is:',
        Notification.permission,
        '. Click anywhere on the app to prompt for notification permission.'
      );
      if (Notification.permission === 'default') {
        this.requestNotificationPermission();
      }
      return;
    }

    try {
      // HIPAA Compliance: Do not expose channel name or message content in push notifications.
      const title = 'SD books';
      const bodyText = 'You have a new message in SD books';
      const iconUrl = message?.sender_avatar || this.getInitialsAvatarDataUrl('SD');
      const options: NotificationOptions & { renotify?: boolean } = {
        body: bodyText,
        icon: iconUrl,
        tag: `chat_channel_${channelId}`,
        renotify: true,
        data: { channelId }
      };

      this.createWindowNotification(title, options, channelId);
    } catch (err) {
      console.error('[Chat WS] Error triggering desktop notification:', err);
    }
  }

  private createWindowNotification(title: string, options: any, channelId: number): void {
    try {
      const notification = new Notification(title, options);
      console.log('[Chat WS] Desktop notification created via Window Notification API');

      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();
        this.openFooterChatSubject.next({ channelId });
        notification.close();
      };
    } catch (err) {
      console.error('[Chat WS] Error instantiating Notification object:', err);
    }
  }

  private getInitialsAvatarDataUrl(name: string): string {
    const parts = (name || 'User').trim().split(/\s+/);
    let initials = 'U';
    if (parts.length >= 2) {
      initials = (parts[0][0] + parts[1][0]).toUpperCase();
    } else if (parts[0]) {
      initials = parts[0].substring(0, 2).toUpperCase();
    }
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 128 128">
      <circle cx="64" cy="64" r="64" fill="#ee7444"/>
      <text x="50%" y="54%" dominant-baseline="middle" text-anchor="middle" fill="#ffffff" font-family="Arial, sans-serif" font-size="52" font-weight="bold">${initials}</text>
    </svg>`;
    return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
  }

  // ───────────────────────────────────────────────────────────────────────────
  // WebSocket heartbeat
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Start WebSocket heartbeat.
   *
   * This is NOT the same thing as navigator.onLine.
   *
   * navigator.onLine:
   *   "Does the device have a network?"
   *
   * ping/pong:
   *   "Can the chat backend actually communicate with us?"
   */
  private startPing(): void {
    this.stopPing();

    this.lastPongAt = Date.now();

    this.pingSubscription =
      interval(
        this.PING_INTERVAL
      ).subscribe(() => {
        if (
          !this.isWebSocketConnected()
        ) {
          return;
        }

        console.log(
          '[Chat WS] Sending ping'
        );

        this._sendWs({
          type: 'ping',
        });

        this.waitForPong();
      });
  }

  /**
   * Wait for the pong response after a heartbeat.
   */
  private waitForPong(): void {
    clearTimeout(
      this.pongTimeoutTimer
    );

    this.pongTimeoutTimer =
      setTimeout(() => {
        if (
          !this.isWebSocketConnected()
        ) {
          return;
        }

        const timeSinceLastPong =
          Date.now() -
          this.lastPongAt;

        if (
          timeSinceLastPong >=
          this.PONG_TIMEOUT
        ) {
          console.warn(
            '[Chat WS] No pong received. WebSocket may be unavailable.'
          );

          this.handleUnresponsiveConnection();
        }
      }, this.PONG_TIMEOUT);
  }

  private stopPing(): void {
    this.pingSubscription?.unsubscribe();

    this.pingSubscription =
      undefined;

    clearTimeout(
      this.pongTimeoutTimer
    );

    this.pongTimeoutTimer =
      undefined;
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Channels
  // ───────────────────────────────────────────────────────────────────────────

  getMyChannels(
    page = 1,
    pageSize = 50,
    channelType?: ChannelType,
    search?: string
  ): Observable<PaginatedChannels> {
    const headers =
      this.getHeaders();

    if (!headers) {
      return EMPTY;
    }

    let url =
      `${this.chatBaseUrl}/api/chat/channels?page=${page}&page_size=${pageSize}`;
    if (channelType) {
      url += `&channel_type=${channelType}`;
    }
    const trimmedSearch = search?.trim();
    if (trimmedSearch) {
      url += `&search=${encodeURIComponent(trimmedSearch)}`;
    }

    return this.http.get<PaginatedChannels>(
      url,
      { headers }
    );
  }

  getChannelDetail(
    channelId: number
  ): Observable<ChannelDetail> {
    const headers =
      this.getHeaders();

    if (!headers) {
      return EMPTY;
    }

    return this.http.get<ChannelDetail>(
      `${this.chatBaseUrl}/api/chat/channels/${channelId}`,
      { headers }
    );
  }

  createChannel(
    request: CreateChannelRequest
  ): Observable<{
    success: boolean;
    channel_id: number;
    message: string;
  }> {
    const headers =
      this.getHeaders();

    if (!headers) {
      return EMPTY;
    }

    return this.http.post<{
      success: boolean;
      channel_id: number;
      message: string;
    }>(
      `${this.chatBaseUrl}/api/chat/channels`,
      request,
      { headers }
    );
  }

  findEntityChannels(
    entityType: string,
    entityId: string
  ): Observable<Channel[]> {
    const headers =
      this.getHeaders();

    if (!headers) {
      return EMPTY;
    }

    return this.http.get<Channel[]>(
      `${this.chatBaseUrl}/api/chat/entities/${entityType}/${entityId}/channels`,
      { headers }
    );
  }

  addMembersToChannel(
    channelId: number,
    userIds: number[]
  ): Observable<{
    success: boolean;
    message: string;
  }> {
    const headers =
      this.getHeaders();

    if (!headers) {
      return EMPTY;
    }

    return this.http.post<{
      success: boolean;
      message: string;
    }>(
      `${this.chatBaseUrl}/api/chat/channels/${channelId}/members`,
      {
        user_ids: userIds,
        role: 'member',
      },
      { headers }
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Mark channel read
  // ───────────────────────────────────────────────────────────────────────────

  markChannelRead(
    channelId: number,
    lastReadMessageId: number
  ): Observable<{
    success: boolean;
    via: 'ws';
  }> {
    const payload = {
      type: 'mark_read',
      channel_id: channelId,
      last_read_message_id:
        lastReadMessageId,
      client_id:
        this.generateClientId(),
    };

    return new Observable(
      (subscriber) => {
        const run = async () => {
          const ready =
            await this.waitUntilAuthenticated();

          if (
            !ready ||
            !this.isWebSocketConnected()
          ) {
            subscriber.error(
              new Error(
                'WebSocket not connected'
              )
            );

            return;
          }

          this._sendWs(payload);

          subscriber.next({
            success: true,
            via: 'ws',
          });

          subscriber.complete();
        };

        run();
      }
    );
  }

  renameChannel(
    channelId: number,
    name: string
  ): Observable<{
    success: boolean;
    message: string;
  }> {
    const headers =
      this.getHeaders();

    if (!headers) {
      return EMPTY;
    }

    return this.http.patch<{
      success: boolean;
      message: string;
    }>(
      `${this.chatBaseUrl}/api/chat/channels/${channelId}`,
      {
        channel_name: name,
      },
      { headers }
    );
  }

  closeChannel(
    channelId: number
  ): Observable<{
    success: boolean;
    message: string;
  }> {
    const headers =
      this.getHeaders();

    if (!headers) {
      return EMPTY;
    }

    return this.http.patch<{
      success: boolean;
      message: string;
    }>(
      `${this.chatBaseUrl}/api/chat/channels/${channelId}`,
      {
        is_archived: true,
      },
      { headers }
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Messages
  // ───────────────────────────────────────────────────────────────────────────

  getMessages(
    channelId: number,
    page = 1,
    pageSize = 50,
    parentMessageId?: number
  ): Observable<PaginatedMessages> {
    const headers =
      this.getHeaders();

    if (!headers) {
      return EMPTY;
    }

    let url =
      `${this.baseUrl}/api/chat/channels/${channelId}/messages?page=${page}&page_size=${pageSize}&sort_order=desc`;

    if (
      parentMessageId != null
    ) {
      url +=
        `&parent_message_id=${parentMessageId}`;
    }

    return this.http.get<PaginatedMessages>(
      url,
      { headers }
    );
  }

  /**
   * True only when the WebSocket is authenticated and OPEN.
   */
  isWebSocketConnected(): boolean {
    return (
      this.wsConnected &&
      this.wsAuthenticated &&
      this.ws?.readyState ===
        WebSocket.OPEN
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Send message
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Send a chat message over WebSocket.
   *
   * Flow:
   *   1. Always track in pendingMessages until message_ack / new_message
   *   2. If WS ready → send immediately (via: 'ws')
   *   3. If not → keep queued and reconnect (via: 'queued')
   *
   * client_id is preserved for optimistic UI reconciliation.
   */
  sendMessage(
    channelId: number,
    request: SendMessageRequest
  ): Observable<{
    success: boolean;
    via: 'ws' | 'queued';
  }> {
    const clientId =
      request.client_id ||
      this.generateClientId();

    const payload: WsSendMessagePayload = {
      type: 'send_message',
      channel_id: channelId,
      message_text:
        request.message_text,
      message_type:
        request.message_type ?? 'text',
      client_id: clientId,
      mentions:
        request.mentions ?? [],
      parent_message_id:
        request.parent_message_id ??
        null,
      attachment_url:
        request.attachment_url ??
        null,
      attachment_type:
        request.attachment_type ??
        null,
      attachment_name:
        request.attachment_name ??
        null,
    };

    return new Observable(
      (subscriber) => {
        const run = async () => {
          /**
           * Keep until server ack so a disconnect mid-flight does not lose it.
           */
          this.queuePendingMessage(payload);

          const ready =
            await this.waitUntilAuthenticated();

          if (
            ready &&
            this.isWebSocketConnected()
          ) {
            this._sendWs(payload);

            subscriber.next({
              success: true,
              via: 'ws',
            });

            subscriber.complete();

            return;
          }

          console.warn(
            '[Chat WS] Connection unavailable. Message remains queued.',
            clientId
          );

          subscriber.next({
            success: true,
            via: 'queued',
          });

          subscriber.complete();

          if (
            !this.manualDisconnect &&
            navigator.onLine &&
            this.getAuthContext()
          ) {
            this.scheduleReconnect();
          }
        };

        run();
      }
    );
  }

  /**
   * Retry a queued message by client_id (same payload, no duplicate queue entry).
   */
  retryOutboundMessage(
    clientId: string
  ): Observable<{ success: boolean; via: 'ws' | 'queued' }> {
    const item = this.pendingMessages.find(
      (m) => m.client_id === clientId
    );

    if (!item) {
      return new Observable((subscriber) => {
        subscriber.error(
          new Error('Message not found in outbound queue')
        );
      });
    }

    return new Observable((subscriber) => {
      const run = async () => {
        const ready = await this.waitUntilAuthenticated();

        if (ready && this.isWebSocketConnected()) {
          this._sendWs(item.payload);
          subscriber.next({ success: true, via: 'ws' });
          subscriber.complete();
          return;
        }

        subscriber.next({ success: true, via: 'queued' });
        subscriber.complete();

        if (
          !this.manualDisconnect &&
          navigator.onLine &&
          this.getAuthContext()
        ) {
          this.scheduleReconnect();
        }
      };
      run();
    });
  }

  /** Outbound queue entries for a channel (restore UI after refresh). */
  getQueuedMessagesForChannel(
    channelId: number
  ): QueuedOutboundMessage[] {
    return this.pendingMessages.filter(
      (m) => m.channel_id === channelId
    );
  }

  /**
   * Fetch messages newer than sinceMessageId after reconnect (gap fill).
   * API returns newest-first; result is ascending for merge into the thread.
   */
  syncChannelGap(
    channelId: number,
    sinceMessageId: number
  ): Observable<Message[]> {
    return this.getMessages(channelId, 1, 50).pipe(
      map((res) =>
        res.messages
          .filter((m) => m != null && m.id > sinceMessageId)
          .reverse()
      )
    );
  }

  /**
   * Add a message to the pending queue only once.
   */
  private queuePendingMessage(
    payload: WsSendMessagePayload
  ): void {
    const alreadyQueued =
      this.pendingMessages.some(
        (item) =>
          item.client_id ===
          payload.client_id
      );

    if (alreadyQueued) {
      return;
    }

    this.pendingMessages.push({
      client_id: payload.client_id,
      channel_id: payload.channel_id,
      payload,
      queued_at: new Date().toISOString(),
    });

    this.persistOutboundQueue();

    console.log(
      '[Chat WS] Message queued',
      payload.client_id
    );
  }

  private removeFromPendingQueue(clientId: string): void {
    const before = this.pendingMessages.length;
    this.pendingMessages = this.pendingMessages.filter(
      (m) => m.client_id !== clientId
    );
    if (this.pendingMessages.length !== before) {
      this.persistOutboundQueue();
      console.log('[Chat WS] Removed from queue after ack', clientId);
    }
  }

  /**
   * Re-send all messages still waiting for ack (do not remove until ack).
   */
  private flushPendingMessages(): void {
    if (!this.isWebSocketConnected()) {
      return;
    }

    if (this.pendingMessages.length === 0) {
      return;
    }

    console.log(
      `[Chat WS] Flushing ${this.pendingMessages.length} pending message(s)`
    );

    for (const item of [...this.pendingMessages]) {
      if (this.isWebSocketConnected()) {
        console.log(
          '[Chat WS] Sending pending message',
          item.client_id
        );
        this._sendWs(item.payload);
      }
    }
  }

  private loadOutboundQueue(): void {
    try {
      const raw = localStorage.getItem(
        ChatService.OUTBOUND_QUEUE_KEY
      );
      if (!raw) {
        this.pendingMessages = [];
        return;
      }
      const parsed = JSON.parse(raw) as QueuedOutboundMessage[];
      this.pendingMessages = Array.isArray(parsed) ? parsed : [];
    } catch {
      this.pendingMessages = [];
    }
  }

  private persistOutboundQueue(): void {
    try {
      localStorage.setItem(
        ChatService.OUTBOUND_QUEUE_KEY,
        JSON.stringify(this.pendingMessages)
      );
    } catch {
      /* storage full or unavailable */
    }
  }

  generateClientId(): string {
    return `client_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 10)}`;
  }

  updateMessage(
    messageId: number,
    messageText: string
  ): Observable<any> {
    const headers =
      this.getHeaders();

    if (!headers) {
      return EMPTY;
    }

    return this.http.put<any>(
      `${this.chatBaseUrl}/api/chat/messages/${messageId}`,
      {
        message_text: messageText,
      },
      { headers }
    );
  }

  deleteMessage(
    messageId: number
  ): Observable<any> {
    const headers =
      this.getHeaders();

    if (!headers) {
      return EMPTY;
    }

    return this.http.delete<any>(
      `${this.chatBaseUrl}/api/chat/messages/${messageId}`,
      { headers }
    );
  }

  addReaction(
    messageId: number,
    request: AddReactionRequest
  ): Observable<any> {
    const headers =
      this.getHeaders();

    if (!headers) {
      return EMPTY;
    }

    return this.http.post<any>(
      `${this.chatBaseUrl}/api/chat/messages/${messageId}/reactions`,
      request,
      { headers }
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Reactions over WebSocket
  // ───────────────────────────────────────────────────────────────────────────

  sendReaction(
    messageId: number,
    emoji: string,
    action: 'add' | 'remove'
  ): Observable<{
    success: boolean;
    via: 'ws';
  }> {
    const payload = {
      type:
        action === 'remove'
          ? 'remove_reaction'
          : 'add_reaction',
      message_id: messageId,
      emoji,
      action,
    };

    return new Observable(
      (subscriber) => {
        const run = async () => {
          const ready =
            await this.waitUntilAuthenticated();

          if (
            !ready ||
            !this.isWebSocketConnected()
          ) {
            subscriber.error(
              new Error(
                'WebSocket not connected'
              )
            );

            return;
          }

          this._sendWs(payload);

          subscriber.next({
            success: true,
            via: 'ws',
          });

          subscriber.complete();
        };

        run();
      }
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Typing
  // ───────────────────────────────────────────────────────────────────────────

  sendTyping(
    channelId: number,
    isTyping: boolean
  ): void {
    if (
      !this.isWebSocketConnected()
    ) {
      return;
    }

    this._sendWs({
      type: 'typing',
      channel_id: channelId,
      is_typing: isTyping,
    });
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Entity resolution
  // ───────────────────────────────────────────────────────────────────────────

  resolveEntity(
    entityType: string,
    entityId: string
  ): Observable<EntityResolveResult> {
    const headers =
      this.getHeaders();

    if (!headers) {
      return EMPTY;
    }

    return this.http.get<EntityResolveResult>(
      `${this.baseUrl}/api/entity-registry/${entityType}/${entityId}/resolve`,
      { headers }
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  // File attachments
  // ───────────────────────────────────────────────────────────────────────────

  uploadAttachment(
    file: File,
    channelId: number
  ): Observable<{
    object_key: string;
    url: string;
  }> {
    const userData =
      this.getAuthContext();

    if (!userData) {
      return EMPTY;
    }

    const headers =
      new HttpHeaders({
        'x-company-id':
          userData.companyId,
      });

    const formData =
      new FormData();

    formData.append(
      'file',
      file
    );

    formData.append(
      'object_key',
      `chat/${channelId}/${Date.now()}_${file.name}`
    );

    formData.append(
      'tags',
      `chat_channel:${channelId}`
    );

    return this.http.post<{
      object_key: string;
      url: string;
    }>(
      `${this.baseUrl}/storage/upload`,
      formData,
      { headers }
    );
  }

  getPresignedDownloadUrl(
    objectKey: string
  ): Observable<{
    presigned_url: string;
    mime_type: string;
  }> {
    const headers =
      this.getHeaders();

    if (!headers) {
      return EMPTY;
    }

    return this.http.get<{
      presigned_url: string;
      mime_type: string;
    }>(
      `${this.baseUrl}/storage/presigned-url/download/${encodeURIComponent(objectKey)}`,
      { headers }
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Entity-linked chat navigation
  // ───────────────────────────────────────────────────────────────────────────

  openChatModal(
    channelId?: number
  ): void {
    this.openChatModal$.next(
      channelId ?? null
    );
  }

  createEntityLinkedChannel(
    entityType: string,
    entityId: string,
    channelName?: string,
    memberIds?: number[]
  ): Observable<number> {
    return this.createChannel({
      channel_type: 'entity_linked',
      linked_entity_type: entityType,
      linked_entity_id: entityId,
      channel_name:
        channelName ||
        `${entityType} #${entityId}`,
      member_ids: memberIds || [],
    }).pipe(
      map((res) => res.channel_id)
    );
  }

  openEntityChat(
    entityType: string,
    entityId: string | number,
    channelName?: string,
    memberIds?: number[],
    options?: { forceCreate?: boolean }
  ): void {
    const targetEntityId = String(entityId);
    if (options?.forceCreate) {
      this.createEntityLinkedChannel(
        entityType,
        targetEntityId,
        channelName,
        memberIds
      ).subscribe({
        next: (channelId) => {
          this.openFooterChat(channelId, {
            channelsSubTab: 'entity_linked',
          });
        },
      });
      return;
    }

    this.findEntityChannels(
      entityType,
      targetEntityId
    ).subscribe({
      next: (channels) => {
        if (channels && channels.length > 0) {
          this.openFooterChat(channels[0].id, {
            channelsSubTab: 'entity_linked',
          });
        } else {
          this.createEntityLinkedChannel(
            entityType,
            targetEntityId,
            channelName,
            memberIds
          ).subscribe({
            next: (channelId) => {
              this.openFooterChat(channelId, {
                channelsSubTab: 'entity_linked',
              });
            },
          });
        }
      },
    });
  }

  searchUsers(
    query: string,
    pageNo: number = 1,
    pageSize: number = 50
  ): Observable<{
    users: {
      user_id: number;
      user_name: string;
      email: string;
      role_name: string;
    }[];
    last: boolean;
    totalElements: number;
    page: number;
  }> {
    const headers = this.getHeaders();

    if (!headers) {
      return EMPTY;
    }

    const params = new URLSearchParams({
      search: query,
      isActive: 'true',
      pageNo: String(pageNo),
      pageSize: String(pageSize),
    });

    return this.http
      .get<any>(`${this.searchUsersUrl}user?${params.toString()}`, { headers })
      .pipe(
        map((response) => {
          const pageData = response?.data ?? response ?? {};
          const users = (pageData.content ?? []).map((user: any) => ({
            user_id: user.userId,
            user_name: user.fullName?.trim() ?? '',
            email: user.email ?? '',
            role_name: this.formatRoleName(user.roleName),
          }));

          return {
            users,
            last: pageData.last ?? true,
            totalElements: pageData.totalElements ?? users.length,
            page: pageNo,
          };
        })
      );
  }

  private formatRoleName(roleName: string | null | undefined): string {
    if (!roleName) {
      return '';
    }

    return roleName
      .replace(/^ROLE_/, '')
      .replace(/_/g, ' ')
      .trim();
  }
  // ───────────────────────────────────────────────────────────────────────────
  // Unread counters
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Load channels + WS subscriptions once so footer badges keep working
   * while the channel-list panel is closed (*ngIf destroyed).
   */
  bootstrapUnreadTracking(): void {
    if (
      this.unreadTrackingBootstrapped ||
      !this.hasAuthContext()
    ) {
      return;
    }

    this.unreadTrackingBootstrapped = true;

    this.getMyChannels().subscribe({
      next: (res) => {
        this.updateUnreadCounts(res.channels);
        res.channels.forEach((ch) =>
          this.subscribeToChannel(ch.id)
        );
      },
      error: () => {
        // Allow retry on next dock init / reconnect.
        this.unreadTrackingBootstrapped = false;
      },
    });
  }

  updateUnreadCounts(
    channels: Channel[]
  ): void {
    const map = new Map(
      this.unreadCounts$.value
    );

    channels.forEach((c) => {
      map.set(
        c.id,
        c.unread_count || 0
      );
      this.channelTypeById.set(
        c.id,
        c.channel_type
      );
    });

    this.unreadCounts$.next(map);
    this.recomputeCategoryTotals();
  }

  /**
   * Increment unread for a channel (used when panel is closed and on WS).
   */
  incrementUnread(channelId: number): void {
    if (channelId === this.activeChannelId && this.activeChannelVisible) {
      return;
    }

    const map = new Map(
      this.unreadCounts$.value
    );
    map.set(
      channelId,
      (map.get(channelId) || 0) + 1
    );
    this.unreadCounts$.next(map);

    if (!this.channelTypeById.has(channelId)) {
      this.getChannelDetail(channelId).subscribe({
        next: (detail) => {
          this.channelTypeById.set(
            channelId,
            detail.channel.channel_type
          );
          this.recomputeCategoryTotals();
        },
      });
    }

    this.recomputeCategoryTotals();
  }

  clearUnread(
    channelId: number
  ): void {
    const current = new Map(
      this.unreadCounts$.value
    );

    if ((current.get(channelId) || 0) === 0) {
      return;
    }

    current.set(channelId, 0);
    this.unreadCounts$.next(current);
    this.recomputeCategoryTotals();
  }

  getUnreadCount(channelId: number): number {
    return this.unreadCounts$.value.get(channelId) || 0;
  }

  private recomputeCategoryTotals(): void {
    let directUnread = 0;
    let channelUnread = 0;

    this.unreadCounts$.value.forEach(
      (count, channelId) => {
        if (!count) {
          return;
        }

        const type =
          this.channelTypeById.get(channelId);

        if (type === 'direct') {
          directUnread += count;
        } else {
          // group / entity_linked / unknown (until detail fetch resolves)
          channelUnread += count;
        }
      }
    );

    this.directUnread$.next(directUnread);
    this.channelUnread$.next(channelUnread);
    this.totalUnread$.next(
      directUnread + channelUnread
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Footer chat / channel activity
  // ───────────────────────────────────────────────────────────────────────────

  openFooterChat(
    channelId: number | null = null,
    options?: { channelsSubTab?: FooterChannelsSubTab }
  ): void {
    this.openFooterChatSubject.next({
      channelId,
      channelsSubTab: options?.channelsSubTab,
    });
  }

  setActiveChannel(
    channelId: number | null,
    visible = false
  ): void {
    this.activeChannelId = channelId;
    this.activeChannelVisible = !!channelId && visible;
  }

  notifyChannelActivity(
    channelId: number,
    lastMessage: string,
    lastMessageAt: string
  ): void {
    this.channelActivitySubject.next({
      channelId,
      lastMessage,
      lastMessageAt,
    });
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Lifecycle
  // ───────────────────────────────────────────────────────────────────────────

  ngOnDestroy(): void {
    window.removeEventListener(
      'online',
      this.handleNetworkOnline
    );

    window.removeEventListener(
      'offline',
      this.handleNetworkOffline
    );

    this.disconnectWebSocket();

    this.connectionStatus$.complete();
    this.reconnected$.complete();
    this.wsEvents$.complete();
    this.unreadCounts$.complete();
    this.totalUnread$.complete();
    this.openChatModal$.complete();
    this.directUnread$.complete();
    this.channelUnread$.complete();
    this.openFooterChatSubject.complete();
    this.channelActivitySubject.complete();
  }
}